import { useState } from 'react'

import { ActionIcon, Tooltip } from '@mantine/core'
import { RefreshCw } from 'lucide-react'

import { toast } from 'src/components/Overlays/Toast/Toast'

/**
 * Re-runs the drawer's own refetch so an automated email or agent call that
 * landed while the drawer was open shows up without a page reload. Spins
 * until the refetch settles and ignores clicks while it runs.
 */
const AuditTrailRefreshButton = ({
  onRefresh,
}: {
  onRefresh: () => Promise<unknown>
}) => {
  const [refreshing, setRefreshing] = useState(false)

  const handleClick = async () => {
    if (refreshing) return
    setRefreshing(true)
    try {
      await onRefresh()
    } catch {
      toast('Unable to refresh, please try again.', 'error')
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <Tooltip label="Refresh" position="bottom-end">
      <ActionIcon
        aria-label="Refresh"
        aria-busy={refreshing}
        variant="subtle"
        color="gray"
        size="sm"
        onClick={handleClick}
        className="h-6 w-6 hover:bg-doubleNickel-gray-100"
      >
        <RefreshCw
          size={14}
          data-testid="audit-trail-refresh-icon"
          className={`stroke-doubleNickel-gray-600 ${
            refreshing ? 'animate-spin' : ''
          }`}
        />
      </ActionIcon>
    </Tooltip>
  )
}

export default AuditTrailRefreshButton
